/* eslint-disable @typescript-eslint/no-dynamic-delete */
/* eslint-disable no-console */
import type mapboxgl from "mapbox-gl";
import {
  calculateStripAreas,
  createAndDrawStripLines,
  generateParallelLines,
  type ABLine,
  type StripLineResult,
} from "./parallelLinesLayer";
export const addStripLinesToMap = (
  map: React.MutableRefObject<mapboxgl.Map | null>,
  abLine: any,
  offsetDistances: number[],
  polygonGeometry: any, // Field polygon geometry
  sourceId: string = "strip-lines",
  options?: {
    lineColor?: string;
    lineWidth?: number;
    stripOpacity?: number;
    stripColors?: string[];
    showLabels?: boolean;
    onStripLineAdjust?: (data: {
      direction: "left" | "right";
      indexOfStripLine: number;
      action?: "step" | "snap";
      snappedDistance?: number;
    }) => void;
  }
): StripLineResult | null => {
  if (!map.current) {
    return null;
  }

  // Validate abLine structure
  if (!abLine) {
    console.error("❌ abLine is null/undefined");
    return null;
  }

  if (!abLine.geojson?.geometry?.coordinates) {
    console.error("❌ abLine structure is invalid:", {
      hasGeojson: !!abLine.geojson,
      hasGeometry: !!abLine.geojson?.geometry,
      hasCoordinates: !!abLine.geojson?.geometry?.coordinates,
    });
    return null;
  }

  const coordinates = abLine.geojson.geometry.coordinates;
  if (!Array.isArray(coordinates) || coordinates.length < 2) {
    console.error("❌ abLine coordinates are invalid:", coordinates);
    return null;
  }

  // Validate polygon geometry
  if (!polygonGeometry) {
    console.error("❌ polygonGeometry is null/undefined");
    return null;
  }

  try {
    // Convert the polygon geometry to the expected format
    let polygon = polygonGeometry.geometry || polygonGeometry;

    if (polygon.features && polygon.features.length > 0) {
      // Extract the actual polygon geometry from the FeatureCollection
      polygon = polygon.features[0].geometry;
    } else {
      console.error("❌ Polygon format not recognized:", polygon);
      return null;
    }

    // Create and draw strip lines
    const result = createAndDrawStripLines(
      map.current,
      abLine,
      offsetDistances,
      polygon,
      sourceId,
      options
    );

    return result;
  } catch (error) {
    console.error("❌ Error in addStripLinesToMap:", error);
    if (error instanceof Error) {
      console.error("❌ Error message:", error.message);
      console.error("❌ Error stack:", error.stack);
    }
    return null;
  }
};

/**
 * Remove strip lines from the map
 * @param map - Mapbox map instance
 * @param sourceId - Base source ID for the layers
 */
// Maps each possible strip-line click/hover handler flag to the (event,
// layerId) pairs it was originally bound with. A flag with no layerId
// (e.g. the divider's window-level move/up handlers) was bound directly via
// `map.off(event, handler)`.
type HandlerOffTarget = readonly [event: string, layerId?: string];

function buildHandlerOffConfig(
  sourceId: string
): Record<string, readonly HandlerOffTarget[]> {
  const controls = `${sourceId}-controls`;
  const controlsDivider = `${sourceId}-controls-divider`;
  const controlsLeftArrow = `${sourceId}-controls-left-arrow`;
  const controlsRightArrow = `${sourceId}-controls-right-arrow`;

  return {
    clickLeft: [["click", controlsLeftArrow]],
    clickRight: [["click", controlsRightArrow]],
    clickCircle: [["click", controls]],
    mouseenterLeft: [["mouseenter", controlsLeftArrow]],
    mouseleaveLeft: [["mouseleave", controlsLeftArrow]],
    mouseenterRight: [["mouseenter", controlsRightArrow]],
    mouseleaveRight: [["mouseleave", controlsRightArrow]],
    mouseenterCircle: [["mouseenter", controls]],
    mouseleaveCircle: [["mouseleave", controls]],
    dividerMouseDown: [
      ["mousedown", controls],
      ["mousedown", controlsDivider],
    ],
    dividerTouchStart: [
      ["touchstart", controls],
      ["touchstart", controlsDivider],
    ],
    dividerMouseMove: [["mousemove"]],
    dividerTouchMove: [["touchmove"]],
    dividerMouseUp: [["mouseup"]],
    dividerTouchEnd: [["touchend"]],
    generalClick: [["click"]],
  };
}

/** Unbinds every registered click/hover handler for one source's controls. */
function removeRegisteredClickHandlers(
  map: mapboxgl.Map,
  handlers: Record<string, any>,
  sourceId: string
): void {
  const config = buildHandlerOffConfig(sourceId);
  Object.keys(config).forEach((key) => {
    const handlerFn = handlers[key];
    if (!handlerFn) return;
    config[key].forEach(([event, layerId]) => {
      if (layerId) {
        map.off(event as any, layerId, handlerFn);
      } else {
        map.off(event as any, handlerFn);
      }
    });
  });
}

/** Removes the click/hover handlers registered by addStripLinesToMap for sourceId. */
function cleanupStripLineClickHandlers(
  map: mapboxgl.Map,
  handlersStore: Record<string, any> | undefined,
  sourceId: string
): void {
  const existingHandlers = handlersStore ?? {};
  if (!existingHandlers[sourceId]) return;

  try {
    removeRegisteredClickHandlers(map, existingHandlers[sourceId], sourceId);
  } catch (error) {
    console.warn(
      "Error removing event handlers in removeStripLinesFromMap:",
      error
    );
  }
  delete existingHandlers[sourceId];
}

/** Removes the strip mouseenter/mouseleave hover handlers for sourceId. */
function cleanupStripHoverHandlers(
  map: mapboxgl.Map,
  hoverHandlersStore: Record<string, any> | undefined,
  sourceId: string
): void {
  const existingStripHoverHandlers = hoverHandlersStore ?? {};
  if (!existingStripHoverHandlers[sourceId]) return;

  try {
    map.off(
      "mouseenter",
      `${sourceId}-strips`,
      existingStripHoverHandlers[sourceId].mouseenter
    );
    map.off(
      "mouseleave",
      `${sourceId}-strips`,
      existingStripHoverHandlers[sourceId].mouseleave
    );
  } catch (error) {
    console.warn(
      "Error removing strip hover handlers in removeStripLinesFromMap:",
      error
    );
  }
  delete existingStripHoverHandlers[sourceId];
}

/** Removes every map layer created for sourceId's strip lines, if present. */
function removeStripLineLayers(map: mapboxgl.Map, sourceId: string): void {
  const layerIds = [
    `${sourceId}-lines`,
    `${sourceId}-line-labels`,
    `${sourceId}-strips`,
    `${sourceId}-strips-bbox`,
    `${sourceId}-strip-area-labels`,
    `${sourceId}-labels`,
    `${sourceId}-controls`,
    `${sourceId}-controls-divider`,
    `${sourceId}-controls-left-arrow`,
    `${sourceId}-controls-right-arrow`,
  ];

  layerIds.forEach((layerId) => {
    if (map.getLayer(layerId)) {
      map.removeLayer(layerId);
    }
  });
}

/** Removes every map source created for sourceId's strip lines, if present. */
function removeStripLineSources(map: mapboxgl.Map, sourceId: string): void {
  const sourceIds = [
    `${sourceId}-lines-source`,
    `${sourceId}-strips-source`,
    `${sourceId}-strips-bbox-source`,
    `${sourceId}-strip-area-labels-source`,
    `${sourceId}-controls-source`,
    `${sourceId}-controls-divider-source`,
  ];

  sourceIds.forEach((srcId) => {
    if (map.getSource(srcId)) {
      map.removeSource(srcId);
    }
  });
}

export const removeStripLinesFromMap: any = (
  map: React.MutableRefObject<mapboxgl.Map | null>,
  sourceId: string = "strip-lines"
) => {
  if (!map.current) return;

  cleanupStripLineClickHandlers(
    map.current,
    (map.current as any)._stripLineHandlers,
    sourceId
  );
  cleanupStripHoverHandlers(
    map.current,
    (map.current as any)._stripHoverHandlers,
    sourceId
  );
  removeStripLineLayers(map.current, sourceId);
  removeStripLineSources(map.current, sourceId);
};

/**
 * Update strip lines with new offset distances
 * @param map - Mapbox map instance
 * @param abLine - The baseline for strip generation
 * @param offsetDistances - New array of distances in meters
 * @param polygonGeometry - The field polygon
 * @param sourceId - Base source ID for the layers
 * @param options - Styling options
 */
export const updateStripLines = (
  map: React.MutableRefObject<mapboxgl.Map | null>,
  abLine: ABLine,
  offsetDistances: number[],
  polygonGeometry: any,
  sourceId: string = "strip-lines",
  options?: {
    lineColor?: string;
    lineWidth?: number;
    stripOpacity?: number;
    stripColors?: string[];
    showLabels?: boolean;
    onStripLineAdjust?: (data: {
      direction: "left" | "right";
      indexOfStripLine: number;
      action?: "step" | "snap";
      snappedDistance?: number;
    }) => void;
  }
): StripLineResult | null => {
  // Remove existing strip lines first
  removeStripLinesFromMap(map, sourceId);

  // Add new strip lines immediately
  const result = addStripLinesToMap(
    map,
    abLine,
    offsetDistances,
    polygonGeometry,
    sourceId,
    options
  );
  return result;
};

/**
 * Toggle strip lines visibility
 * @param map - Mapbox map instance
 * @param visible - Whether to show or hide the strip lines
 * @param sourceId - Base source ID for the layers
 */
export const toggleStripLinesVisibility: any = (
  map: React.MutableRefObject<mapboxgl.Map | null>,
  visible: boolean,
  sourceId: string = "strip-lines"
) => {
  if (!map.current) return;

  const layerIds = [
    `${sourceId}-lines`,
    `${sourceId}-line-labels`,
    `${sourceId}-strips`,
    `${sourceId}-strips-bbox`,
    `${sourceId}-labels`,
  ];

  layerIds.forEach((layerId) => {
    if (map.current?.getLayer(layerId)) {
      map.current.setLayoutProperty(
        layerId,
        "visibility",
        visible ? "visible" : "none"
      );
    }
  });
};

/**
 * Get strip lines area information
 * @param abLine - The baseline for strip generation
 * @param offsetDistances - Array of distances in meters
 * @param polygonGeometry - The field polygon
 * @returns Area information without rendering to map
 */
export const getStripLinesAreas = (
  abLine: ABLine,
  offsetDistances: number[],
  polygonGeometry: any
): {
  areas: number[];
  totalArea: number;
  stripCount: number;
  lineLengths: number[];
  totalLineLength: number;
} | null => {
  try {
    let polygon = polygonGeometry.geometry || polygonGeometry;

    // Handle FeatureCollection by extracting the first feature's geometry
    if (polygon.features && polygon.features.length > 0) {
      polygon = polygon.features[0].geometry;
    }

    // Generate parallel lines
    const parallelLines = generateParallelLines(
      abLine,
      offsetDistances,
      polygon
    );

    // Calculate strip areas
    const stripResult = calculateStripAreas(parallelLines, polygon);

    const areas = stripResult.strips.map((strip) => strip.area);
    const totalArea = areas.reduce((sum, area) => sum + area, 0);
    const lineLengths = stripResult.lines.map(
      (line) => (line.properties?.length as number) ?? 0
    );
    const totalLineLength = lineLengths.reduce(
      (sum, length) => sum + length,
      0
    );

    return {
      areas,
      totalArea,
      stripCount: stripResult.strips.length,
      lineLengths,
      totalLineLength,
    };
  } catch (error) {
    console.error("Error calculating strip areas:", error);
    return null;
  }
};
